import React, { useState, useEffect } from 'react';
import { getTimeEntries, getProjects, getClients, getTasks, getUsers, deleteTimeEntry, createTimeEntry } from '../lib/api';
import { useTimer } from '../lib/TimerContext';
import { useAuth } from '../context/AuthContext';
import { formatTime, formatCurrency } from '../lib/utils';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { Play, Square, Clock, Trash2, Calendar, User, FolderKanban, Users, Edit, Plus, DollarSign } from 'lucide-react';
import { toast } from 'sonner';
import TimeEntryEditDialog from '../components/TimeEntryEditDialog';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  Legend,
  PieChart,
  Pie,
  Cell,
} from 'recharts';

const COLORS = ['#006CB6', '#F4E600', '#22c55e', '#ef4444', '#8b5cf6', '#ec4899', '#f97316'];

const TimeTracker = () => {
  const { user, hasPermission } = useAuth();
  const { isRunning, timerData, startTimer, stopTimer, formatElapsedTime } = useTimer();
  const isAdmin = user?.is_admin;
  
  const [entries, setEntries] = useState([]);
  const [projects, setProjects] = useState([]);
  const [clients, setClients] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [entryToDelete, setEntryToDelete] = useState(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [entryToEdit, setEntryToEdit] = useState(null);
  const [manualDialogOpen, setManualDialogOpen] = useState(false);
  const [manualData, setManualData] = useState({
    project_id: '',
    task_id: '',
    date: new Date().toISOString().slice(0, 10),
    duration_minutes: '',
    start_time: '',
    end_time: '',
    description: '',
    billable: true,
  });
  const [manualUseRange, setManualUseRange] = useState(false);
  const [manualLoading, setManualLoading] = useState(false);

  // Timer form
  const [selectedProject, setSelectedProject] = useState('');
  const [selectedTask, setSelectedTask] = useState('');
  const [timerDescription, setTimerDescription] = useState('');
  
  // Filters
  const [filterDateFrom, setFilterDateFrom] = useState('');
  const [filterDateTo, setFilterDateTo] = useState('');
  const [filterProject, setFilterProject] = useState('all');
  const [filterClient, setFilterClient] = useState('all');
  const [filterUser, setFilterUser] = useState('all');

  const fetchData = async () => {
    try {
      const [entriesRes, projectsRes, clientsRes, tasksRes, usersRes] = await Promise.all([
        getTimeEntries({ statuses: 'active,completed' }),
        getProjects({}),
        getClients(),
        getTasks({}),
        isAdmin ? getUsers() : Promise.resolve({ data: [] }),
      ]);
      
      // Filter entries for non-admin (only their own)
      let filteredEntries = entriesRes.data;
      if (!isAdmin) {
        filteredEntries = filteredEntries.filter(e => e.user_id === user.id);
      }
      
      setEntries(filteredEntries);
      setProjects(projectsRes.data);
      setClients(clientsRes.data);
      setTasks(tasksRes.data);
      setUsers(usersRes.data || []);
    } catch (error) {
      toast.error('Chyba při načítání dat');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleStartTimer = async () => {
    if (!selectedProject) {
      toast.error('Vyberte projekt');
      return;
    }
    const project = projects.find(p => p.id === selectedProject);
    const task = tasks.find(t => t.id === selectedTask);
    
    try {
      await startTimer({
        project_id: selectedProject,
        project_name: project?.name || '',
        task_id: selectedTask || null,
        task_name: task?.title || '',
        description: timerDescription,
      });
    } catch (error) {
      // Error handled in TimerContext
    }
  };

  const handleStopTimer = async () => {
    try {
      await stopTimer();
      fetchData(); // Refresh entries after stopping
    } catch (error) {
      // Error handled in TimerContext
    }
  };

  // Auto-calculate duration from manual time range
  useEffect(() => {
    if (manualUseRange && manualData.start_time && manualData.end_time && manualData.date) {
      try {
        const start = new Date(`${manualData.date}T${manualData.start_time}`);
        const end = new Date(`${manualData.date}T${manualData.end_time}`);
        const diff = Math.max(1, Math.round((end - start) / 60000));
        if (end > start) setManualData(prev => ({ ...prev, duration_minutes: diff.toString() }));
      } catch {}
    }
  }, [manualUseRange, manualData.start_time, manualData.end_time, manualData.date]);

  const handleManualSave = async () => {
    if (!manualData.project_id) { toast.error('Vyberte projekt'); return; }
    if (!manualData.date) { toast.error('Datum je povinné'); return; }
    const duration = parseInt(manualData.duration_minutes);
    if (!duration || duration < 1) { toast.error('Zadejte dobu trvání (min. 1 min)'); return; }

    setManualLoading(true);
    try {
      const payload = {
        project_id: manualData.project_id,
        task_id: manualData.task_id || null,
        date: manualData.date,
        duration_minutes: duration,
        description: manualData.description || null,
        billable: manualData.billable,
      };
      if (manualUseRange && manualData.start_time && manualData.end_time) {
        payload.start_time = `${manualData.date}T${manualData.start_time}:00`;
        payload.end_time = `${manualData.date}T${manualData.end_time}:00`;
      }
      await createTimeEntry(payload);
      toast.success('Záznam přidán');
      setManualDialogOpen(false);
      setManualData({ project_id: '', task_id: '', date: new Date().toISOString().slice(0, 10), duration_minutes: '', start_time: '', end_time: '', description: '', billable: true });
      setManualUseRange(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Chyba při ukládání');
    } finally {
      setManualLoading(false);
    }
  };

  const handleDeleteEntry = async () => {
    if (!entryToDelete) return;
    try {
      await deleteTimeEntry(entryToDelete.id);
      toast.success('Záznam smazán');
      fetchData();
    } catch (error) {
      toast.error('Chyba při mazání');
    }
    setDeleteDialogOpen(false);
    setEntryToDelete(null);
  };

  // Apply filters + sort newest first
  const filteredEntries = entries.filter(entry => {
    if (filterDateFrom && entry.date < filterDateFrom) return false;
    if (filterDateTo && entry.date > filterDateTo) return false;
    if (filterProject !== 'all' && entry.project_id !== filterProject) return false;
    if (filterUser !== 'all' && entry.user_id !== filterUser) return false;
    if (filterClient !== 'all') {
      const project = projects.find(p => p.id === entry.project_id);
      if (!project || project.client_id !== filterClient) return false;
    }
    return true;
  }).sort((a, b) => {
    if (b.date !== a.date) return b.date.localeCompare(a.date);
    const bt = b.start_time || b.created_at || '';
    const at = a.start_time || a.created_at || '';
    return bt > at ? 1 : -1;
  });

  // Calculate stats
  const totalMinutes = filteredEntries.reduce((sum, e) => sum + (e.duration_minutes || 0), 0);
  
  // Group by project for chart
  const projectTimeData = {};
  filteredEntries.forEach(entry => {
    const name = entry.project_name || 'Neznámý';
    if (!projectTimeData[name]) projectTimeData[name] = 0;
    projectTimeData[name] += entry.duration_minutes || 0;
  });
  const projectChartData = Object.entries(projectTimeData)
    .map(([name, value]) => ({ name, value: Math.round(value / 60 * 10) / 10, minutes: value }))
    .sort((a, b) => b.minutes - a.minutes)
    .slice(0, 7);

  // Group by user for chart (admin only)
  const userTimeData = {};
  filteredEntries.forEach(entry => {
    const name = entry.user_name || 'Neznámý';
    if (!userTimeData[name]) userTimeData[name] = 0;
    userTimeData[name] += entry.duration_minutes || 0;
  });
  const userChartData = Object.entries(userTimeData)
    .map(([name, value]) => ({ name, value: Math.round(value / 60 * 10) / 10, minutes: value }))
    .sort((a, b) => b.minutes - a.minutes);

  // Filter tasks by selected project
  const filteredTasks = selectedProject
    ? tasks.filter(t => t.project_id === selectedProject)
    : tasks;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="font-oswald text-xl uppercase tracking-wide">Načítání...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6 pb-20" data-testid="time-tracker-page">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h1 className="font-oswald text-4xl font-bold uppercase tracking-tight">Time Tracker</h1>
          <p className="font-serif text-gray-600 mt-1">Sledování času a záznamy</p>
        </div>
        <Button
          onClick={() => setManualDialogOpen(true)}
          className="bg-black text-white font-oswald uppercase tracking-wide border-2 border-black rounded-none shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] hover:translate-x-[2px] hover:translate-y-[2px] hover:shadow-[2px_2px_0px_0px_rgba(0,0,0,1)] transition-all"
        >
          <Plus size={16} className="mr-2" />
          Přidat záznam
        </Button>
      </div>

      {/* Timer Section */}
      <div className="bg-white border-2 border-black p-6 shadow-[4px_4px_0px_0px_rgba(0,0,0,1)]">
        <h2 className="font-oswald text-xl font-bold uppercase tracking-tight mb-4 flex items-center gap-2">
          <Clock size={20} />
          Stopky
        </h2>
        
        {isRunning ? (
          <div className="space-y-4">
            <div className="text-center">
              <div className="font-oswald text-6xl tabular-nums tracking-wider mb-2" data-testid="timer-display">
                {formatElapsedTime()}
              </div>
              <div className="font-serif text-gray-500">
                {timerData.project_name}
                {timerData.task_name && ` • ${timerData.task_name}`}
              </div>
            </div>
            <Button
              onClick={handleStopTimer}
              className="w-full bg-red-500 text-white font-oswald uppercase tracking-wide border-2 border-black rounded-none shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] hover:bg-red-600 transition-all"
              data-testid="stop-timer-btn"
            >
              <Square size={18} className="mr-2" />
              Stop & Uložit
            </Button>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="font-oswald uppercase text-xs tracking-wide">Projekt *</Label>
                <Select value={selectedProject} onValueChange={setSelectedProject}>
                  <SelectTrigger className="border-2 border-gray-200 rounded-none" data-testid="timer-project-select">
                    <SelectValue placeholder="Vyberte projekt" />
                  </SelectTrigger>
                  <SelectContent>
                    {projects.filter(p => p.status === 'active' || p.status === 'completed').map((p) => (
                      <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="font-oswald uppercase text-xs tracking-wide">Úkol (volitelně)</Label>
                <Select value={selectedTask || 'none'} onValueChange={(v) => setSelectedTask(v === 'none' ? '' : v)}>
                  <SelectTrigger className="border-2 border-gray-200 rounded-none" data-testid="timer-task-select">
                    <SelectValue placeholder="Vyberte úkol" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Bez úkolu</SelectItem>
                    {filteredTasks.map((t) => (
                      <SelectItem key={t.id} value={t.id}>{t.title}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label className="font-oswald uppercase text-xs tracking-wide">Popis (volitelně)</Label>
              <Input
                value={timerDescription}
                onChange={(e) => setTimerDescription(e.target.value)}
                placeholder="Co děláte?"
                className="border-2 border-gray-200 focus:border-black rounded-none"
                data-testid="timer-description"
              />
            </div>
            <Button
              onClick={handleStartTimer}
              className="w-full bg-[#F4E600] text-black font-oswald uppercase tracking-wide border-2 border-black rounded-none shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] hover:translate-x-[2px] hover:translate-y-[2px] hover:shadow-[2px_2px_0px_0px_rgba(0,0,0,1)] transition-all"
              data-testid="start-timer-btn"
            >
              <Play size={18} className="mr-2" />
              Spustit stopky
            </Button>
          </div>
        )}
      </div>

      {/* Filters */}
      <div className="bg-white border-2 border-black p-4 shadow-[4px_4px_0px_0px_rgba(0,0,0,1)]">
        <h2 className="font-oswald text-lg font-bold uppercase tracking-tight mb-4">Filtry</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
          <div className="space-y-2">
            <Label className="font-oswald uppercase text-xs tracking-wide flex items-center gap-1">
              <Calendar size={12} /> Od
            </Label>
            <Input
              type="date"
              value={filterDateFrom}
              onChange={(e) => setFilterDateFrom(e.target.value)}
              className="border-2 border-gray-200 focus:border-black rounded-none"
              data-testid="filter-date-from"
            />
          </div>
          <div className="space-y-2">
            <Label className="font-oswald uppercase text-xs tracking-wide flex items-center gap-1">
              <Calendar size={12} /> Do
            </Label>
            <Input
              type="date"
              value={filterDateTo}
              onChange={(e) => setFilterDateTo(e.target.value)}
              className="border-2 border-gray-200 focus:border-black rounded-none"
              data-testid="filter-date-to"
            />
          </div>
          <div className="space-y-2">
            <Label className="font-oswald uppercase text-xs tracking-wide flex items-center gap-1">
              <Users size={12} /> Klient
            </Label>
            <Select value={filterClient} onValueChange={setFilterClient}>
              <SelectTrigger className="border-2 border-gray-200 rounded-none">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Všichni klienti</SelectItem>
                {clients.map((c) => (
                  <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label className="font-oswald uppercase text-xs tracking-wide flex items-center gap-1">
              <FolderKanban size={12} /> Projekt
            </Label>
            <Select value={filterProject} onValueChange={setFilterProject}>
              <SelectTrigger className="border-2 border-gray-200 rounded-none">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Všechny projekty</SelectItem>
                {projects.map((p) => (
                  <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {isAdmin && (
            <div className="space-y-2">
              <Label className="font-oswald uppercase text-xs tracking-wide flex items-center gap-1">
                <User size={12} /> Uživatel
              </Label>
              <Select value={filterUser} onValueChange={setFilterUser}>
                <SelectTrigger className="border-2 border-gray-200 rounded-none">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Všichni uživatelé</SelectItem>
                  {users.map((u) => (
                    <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
        </div>
      </div>

      {/* Stats Summary */}
      <div className="bg-white border-2 border-black p-6 shadow-[4px_4px_0px_0px_rgba(0,0,0,1)]">
        <div className="flex items-center justify-between">
          <div>
            <div className="font-oswald text-xs uppercase tracking-wide text-gray-500">Celkový čas</div>
            <div className="font-oswald text-4xl font-bold">{formatTime(totalMinutes)}</div>
          </div>
          <div className="text-right">
            <div className="font-oswald text-xs uppercase tracking-wide text-gray-500">Počet záznamů</div>
            <div className="font-oswald text-4xl font-bold">{filteredEntries.length}</div>
          </div>
        </div>
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* By Project */}
        <div className="bg-white border-2 border-black p-6 shadow-[4px_4px_0px_0px_rgba(0,0,0,1)]">
          <h2 className="font-oswald text-lg font-bold uppercase tracking-tight mb-4">Čas podle projektů</h2>
          {projectChartData.length > 0 ? (
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={projectChartData} layout="vertical">
                  <XAxis type="number" tickFormatter={(v) => `${v}h`} />
                  <YAxis type="category" dataKey="name" width={100} tick={{ fontSize: 11 }} />
                  <Tooltip formatter={(v) => `${v} hodin`} />
                  <Bar dataKey="value" fill="#006CB6" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <div className="h-64 flex items-center justify-center text-gray-400 font-serif">
              Žádná data
            </div>
          )}
        </div>

        {/* By User (Admin only) */}
        {isAdmin && (
          <div className="bg-white border-2 border-black p-6 shadow-[4px_4px_0px_0px_rgba(0,0,0,1)]">
            <h2 className="font-oswald text-lg font-bold uppercase tracking-tight mb-4">Čas podle uživatelů</h2>
            {userChartData.length > 0 ? (
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={userChartData}
                      dataKey="value"
                      nameKey="name"
                      cx="50%"
                      cy="50%"
                      outerRadius={80}
                      label={({ name, value }) => `${name}: ${value}h`}
                    >
                      {userChartData.map((_, index) => (
                        <Cell key={index} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(v) => `${v} hodin`} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <div className="h-64 flex items-center justify-center text-gray-400 font-serif">
                Žádná data
              </div>
            )}
          </div>
        )}
      </div>

      {/* Entries List */}
      <div className="bg-white border-2 border-black p-6 shadow-[4px_4px_0px_0px_rgba(0,0,0,1)]">
        <h2 className="font-oswald text-lg font-bold uppercase tracking-tight mb-4">Záznamy ({filteredEntries.length})</h2>
        {filteredEntries.length === 0 ? (
          <div className="text-center py-8 text-gray-400 font-serif">
            Žádné záznamy pro vybraný filtr
          </div>
        ) : (
          <div className="space-y-2 max-h-96 overflow-y-auto">
            {filteredEntries.map((entry) => {
              const canEdit = isAdmin || entry.user_id === user?.id;
              return (
                <div
                  key={entry.id}
                  className="flex items-center justify-between p-3 border-2 border-gray-200 hover:border-black transition-colors"
                  data-testid={`time-entry-${entry.id}`}
                >
                  <div className="flex-1">
                    <div className="font-oswald font-bold">{entry.project_name}</div>
                    <div className="text-sm text-gray-500 font-serif">
                      {entry.date} • {entry.description || 'Bez popisu'}
                      {isAdmin && entry.user_name && ` • ${entry.user_name}`}
                    </div>
                    {entry.start_time && entry.end_time && (
                      <div className="text-xs text-gray-400 font-serif mt-1">
                        {new Date(entry.start_time).toLocaleTimeString('cs-CZ', { hour: '2-digit', minute: '2-digit' })}
                        {' → '}
                        {new Date(entry.end_time).toLocaleTimeString('cs-CZ', { hour: '2-digit', minute: '2-digit' })}
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="font-oswald text-lg mr-2">{formatTime(entry.duration_minutes)}</div>
                    <button
                      onClick={() => startTimer({
                        project_id: entry.project_id,
                        project_name: entry.project_name,
                        task_id: entry.task_id || null,
                        task_name: entry.task_title || '',
                        description: entry.description || '',
                      })}
                      disabled={isRunning}
                      className="p-2 hover:bg-yellow-50 text-yellow-600 transition-colors disabled:opacity-30"
                      title="Spustit znovu"
                    >
                      <Play size={15} />
                    </button>
                    {canEdit && (
                      <button
                        onClick={() => { setEntryToEdit(entry); setEditDialogOpen(true); }}
                        className="p-2 hover:bg-blue-100 text-blue-500 transition-colors"
                        data-testid={`edit-entry-${entry.id}`}
                        title="Upravit záznam"
                      >
                        <Edit size={16} />
                      </button>
                    )}
                    {canEdit && (
                      <button
                        onClick={() => { setEntryToDelete(entry); setDeleteDialogOpen(true); }}
                        className="p-2 hover:bg-red-100 text-red-500 transition-colors"
                        data-testid={`delete-entry-${entry.id}`}
                        title="Smazat záznam"
                      >
                        <Trash2 size={16} />
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Delete Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent className="border-2 border-black rounded-none shadow-[8px_8px_0px_0px_rgba(0,0,0,1)]">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-oswald uppercase">Smazat záznam?</AlertDialogTitle>
            <AlertDialogDescription className="font-serif">
              Opravdu chcete smazat tento časový záznam? Tato akce je nevratná.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="font-oswald uppercase rounded-none border-2 border-gray-200">
              Zrušit
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteEntry}
              className="bg-red-500 font-oswald uppercase rounded-none hover:bg-red-600"
            >
              Smazat
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Edit Dialog */}
      <TimeEntryEditDialog
        entry={entryToEdit}
        open={editDialogOpen}
        onOpenChange={setEditDialogOpen}
        onEntryUpdated={fetchData}
      />

      {/* Manual Entry Dialog */}
      <Dialog open={manualDialogOpen} onOpenChange={setManualDialogOpen}>
        <DialogContent className="border-2 border-black rounded-none shadow-[8px_8px_0px_0px_rgba(0,0,0,1)] max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-oswald text-xl uppercase tracking-tight">
              Přidat záznam ručně
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 mt-2">
            {/* Project */}
            <div className="space-y-2">
              <Label className="font-oswald uppercase text-xs tracking-wide">Projekt *</Label>
              <Select value={manualData.project_id} onValueChange={(v) => setManualData({ ...manualData, project_id: v, task_id: '' })}>
                <SelectTrigger className="border-2 border-gray-200 rounded-none">
                  <SelectValue placeholder="Vyberte projekt" />
                </SelectTrigger>
                <SelectContent>
                  {projects.filter(p => p.status === 'active' || p.status === 'completed').map(p => (
                    <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Task */}
            <div className="space-y-2">
              <Label className="font-oswald uppercase text-xs tracking-wide">Úkol (volitelně)</Label>
              <Select value={manualData.task_id || 'none'} onValueChange={(v) => setManualData({ ...manualData, task_id: v === 'none' ? '' : v })}>
                <SelectTrigger className="border-2 border-gray-200 rounded-none">
                  <SelectValue placeholder="Bez úkolu" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Bez úkolu</SelectItem>
                  {tasks.filter(t => !manualData.project_id || t.project_id === manualData.project_id).map(t => (
                    <SelectItem key={t.id} value={t.id}>{t.title}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Date */}
            <div className="space-y-2">
              <Label className="font-oswald uppercase text-xs tracking-wide flex items-center gap-1">
                <Calendar size={12} /> Datum *
              </Label>
              <Input
                type="date"
                value={manualData.date}
                onChange={(e) => setManualData({ ...manualData, date: e.target.value })}
                className="border-2 border-gray-200 focus:border-black rounded-none"
              />
            </div>

            {/* Time range toggle */}
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="manual-use-range"
                checked={manualUseRange}
                onChange={(e) => setManualUseRange(e.target.checked)}
                className="rounded border-gray-300"
              />
              <Label htmlFor="manual-use-range" className="font-serif text-sm cursor-pointer">
                Zadat čas od–do
              </Label>
            </div>

            {manualUseRange && (
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="font-oswald uppercase text-xs tracking-wide">Začátek</Label>
                  <Input
                    type="time"
                    value={manualData.start_time}
                    onChange={(e) => setManualData({ ...manualData, start_time: e.target.value })}
                    className="border-2 border-gray-200 focus:border-black rounded-none"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="font-oswald uppercase text-xs tracking-wide">Konec</Label>
                  <Input
                    type="time"
                    value={manualData.end_time}
                    onChange={(e) => setManualData({ ...manualData, end_time: e.target.value })}
                    className="border-2 border-gray-200 focus:border-black rounded-none"
                  />
                </div>
              </div>
            )}

            {/* Duration */}
            <div className="space-y-2">
              <Label className="font-oswald uppercase text-xs tracking-wide flex items-center gap-1">
                <Clock size={12} /> Doba trvání (minuty) *
              </Label>
              <div className="flex items-center gap-2">
                <Input
                  type="number"
                  min="1"
                  value={manualData.duration_minutes}
                  onChange={(e) => setManualData({ ...manualData, duration_minutes: e.target.value })}
                  disabled={manualUseRange}
                  className="border-2 border-gray-200 focus:border-black rounded-none flex-1"
                  placeholder="např. 90"
                />
                <span className="text-sm text-gray-500 font-serif">
                  = {formatTime(parseInt(manualData.duration_minutes) || 0)}
                </span>
              </div>
            </div>

            {/* Description */}
            <div className="space-y-2">
              <Label className="font-oswald uppercase text-xs tracking-wide">Popis</Label>
              <Textarea
                value={manualData.description}
                onChange={(e) => setManualData({ ...manualData, description: e.target.value })}
                placeholder="Co bylo uděláno?"
                className="border-2 border-gray-200 focus:border-black rounded-none"
                rows={2}
              />
            </div>

            {/* Billable (admin only) */}
            {isAdmin && (
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="manual-billable"
                  checked={manualData.billable}
                  onChange={(e) => setManualData({ ...manualData, billable: e.target.checked })}
                  className="rounded border-gray-300"
                />
                <Label htmlFor="manual-billable" className="font-serif text-sm cursor-pointer flex items-center gap-1">
                  <DollarSign size={12} className={manualData.billable ? 'text-green-600' : 'text-gray-400'} />
                  {manualData.billable ? 'Fakturovatelný' : 'Nefakturovatelný'}
                </Label>
              </div>
            )}

            {/* Actions */}
            <div className="flex gap-2 pt-2 border-t border-gray-200">
              <Button
                onClick={handleManualSave}
                disabled={manualLoading}
                className="flex-1 bg-black text-white font-oswald uppercase tracking-wide rounded-none hover:bg-gray-800"
              >
                <Plus size={16} className="mr-2" />
                {manualLoading ? 'Ukládám...' : 'Přidat záznam'}
              </Button>
              <Button
                variant="outline"
                onClick={() => setManualDialogOpen(false)}
                className="font-oswald uppercase rounded-none border-2 border-gray-200"
              >
                Zrušit
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TimeTracker;
